"use client"

import { useState, useEffect, useRef, useMemo } from "react"
import { useAuth } from "@/contexts/auth-context"
import { env } from "@/lib/env"
import { sendMessageToParent } from "@/lib/app-messaging"
import { buildPreVerificationRoute } from "@/lib/pre-verification-route"
import { useParentApp } from "@/contexts/parent-app-context"
import type { SubmitResult } from "@/types/onboarding"
import type { ClientRequestStatus } from "@/types/client-requests"

interface OnboardingIframePanelProps {
  onboardingId: string | null
  isOpen: boolean
  onClose: () => void
  onSubmitted: (result: SubmitResult) => void
  idDisplayText?: string
  sourceName?: string
  recordNoun?: string
  /** The client request's own status, which routes its View Pre-Verification link on the Portal host. */
  clientRequestStatus?: ClientRequestStatus
}

export function OnboardingIframePanel({
  onboardingId,
  isOpen,
  onClose,
  onSubmitted,
  idDisplayText,
  sourceName = "Client Portal",
  recordNoun = "request",
  clientRequestStatus,
}: OnboardingIframePanelProps) {
  const [isVisible, setIsVisible] = useState(false)
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const { token, handleIframeTokenRequest, sendTokenToIframe } = useAuth()
  const { parentApp } = useParentApp()

  const expectedOrigin = useMemo(() => {
    try {
      return env.onboardingFormUrl ? new URL(env.onboardingFormUrl).origin : null
    } catch {
      console.error("OnboardingIframePanel: Invalid env.onboardingFormUrl")
      return null
    }
  }, [])

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        setIsVisible(true)
      }, 10)
      return () => clearTimeout(timer)
    } else {
      setIsVisible(false)
    }
  }, [isOpen])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onClose()
      }
    }
    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [isOpen, onClose])

  useEffect(() => {
    if (!isOpen) return

    const handleMessage = (event: MessageEvent) => {
      try {
        if (!expectedOrigin) {
          console.warn("OnboardingIframePanel: Cannot validate origin - invalid onboarding form URL")
          return
        }
        if (event.origin !== expectedOrigin) {
          console.warn("OnboardingIframePanel: Ignoring message from unexpected origin:", event.origin)
          return
        }

        const data = event.data

        if (data && data.action === "REQUEST_TOKEN" && event.source) {
          handleIframeTokenRequest(event.source as Window, event.origin)
          return
        }

        if (data && data.type === "onboarding:close") {
          onClose()
        } else if (data && data.type === "onboarding:submitted") {
          onSubmitted(data.result as SubmitResult)
        } else if (data && data.type === "onboarding:view-pre-verification") {
          const route = buildPreVerificationRoute(data.requestId, parentApp, data.phase, clientRequestStatus)
          // Sent synchronously: the host opens the new tab only while it still counts as part of the user's click.
          if (route) sendMessageToParent({ action: "NAVIGATE", route, openInNewTab: true })
        }
      } catch (error) {
        console.error("OnboardingIframePanel: Error processing message from iframe:", error)
      }
    }

    window.addEventListener("message", handleMessage)
    return () => window.removeEventListener("message", handleMessage)
  }, [isOpen, expectedOrigin, handleIframeTokenRequest, onClose, onSubmitted, parentApp, clientRequestStatus])

  useEffect(() => {
    if (isOpen && token && iframeRef.current?.contentWindow && expectedOrigin) {
      const timer = setTimeout(() => {
        sendTokenToIframe(iframeRef.current!.contentWindow!, expectedOrigin)
      }, 500)
      return () => clearTimeout(timer)
    }
  }, [isOpen, token, expectedOrigin, sendTokenToIframe])

  const handleIframeLoad = () => {
    if (token && iframeRef.current?.contentWindow && expectedOrigin) {
      sendTokenToIframe(iframeRef.current.contentWindow, expectedOrigin)
    }
  }

  if (!isOpen) return null

  const iframeSrc =
    onboardingId
      ? `${env.onboardingFormUrl}/onboarding-form/?id=${onboardingId}&sourceName=${encodeURIComponent(sourceName)}&recordNoun=${encodeURIComponent(recordNoun)}${idDisplayText ? `&idDisplayText=${encodeURIComponent(idDisplayText)}` : ""}`
      : null

  return (
    <>
      {/* Overlay to prevent interaction outside the panel */}
      <div
        className="fixed inset-0 bg-black/30 transition-opacity duration-300"
        style={{
          zIndex: 1999,
          opacity: isVisible ? 1 : 0,
          pointerEvents: isVisible ? "auto" : "none",
        }}
        aria-hidden="true"
      />

      <div
        role="dialog"
        aria-modal="true"
        className="fixed inset-y-0 right-0 w-[640px] bg-white shadow-xl border-l border-gray-200 flex flex-col font-roboto transition-all duration-300 ease-out outline-none"
        style={{
          zIndex: 2000,
          transform: isVisible ? "translateX(0)" : "translateX(100%)",
          opacity: isVisible ? 1 : 0,
        }}
      >
        <div className="flex-1 overflow-hidden">
          {iframeSrc && (
            <iframe
              ref={iframeRef}
              src={iframeSrc}
              className="w-full h-full border-0"
              onLoad={handleIframeLoad}
              title="Onboarding Form"
            />
          )}
        </div>
      </div>
    </>
  )
}
